/* Nieskończone taśmy z recyklingiem elementów.
   W DOM jest zawsze tylko tyle elementów, ile potrzeba, by wypełnić ekran plus zapas (kilkaset px),
   a nie kilka tysięcy px, jak przy klasycznej taśmie z dwiema kopiami. Dzięki temu przeglądarki mobilne
   (iOS Safari/Chrome) nie muszą utrzymywać ogromnej warstwy i nic nie znika.
   Element, który wyjdzie poza ekran, jest usuwany z jednej strony i dokładany po drugiej w kolejności cyklu.
   Bez JS lub przy „ogranicz ruch” taśma jest zwykłą listą do przewijania palcem. */
(() => {
  'use strict';
  if (window.__komfortMarquee) return;
  window.__komfortMarquee = true;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const BUFFER = 120;
  const tickers = new Set();
  let last = 0;
  let running = false;

  class Ticker {
    constructor(el) {
      this.el = el;
      this.track = el.firstElementChild;
      this.originals = Array.from(this.track.children);
      this.speed = parseFloat(el.dataset.speed) || 30;
      this.dir = el.hasAttribute('data-reverse') ? -1 : 1;
      this.live = false;
      this.hover = false;
      this.visible = true;
      this.lastWidth = -1;

      this.setup();
      if (window.ResizeObserver) {
        new ResizeObserver(() => {
          if (Math.round(this.el.clientWidth) !== this.lastWidth) this.setup();
        }).observe(el);
      }
      if (window.IntersectionObserver) {
        new IntersectionObserver((entries) => {
          this.visible = entries[entries.length - 1].isIntersecting;
        }).observe(el);
      }
      el.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.hover = true; });
      el.addEventListener('pointerleave', () => { this.hover = false; });
    }

    teardown() {
      if (!this.live) return;
      this.track.replaceChildren(...this.originals);
      this.track.style.transform = '';
      this.el.classList.remove('is-live');
      this.live = false;
    }

    setup() {
      this.teardown();
      this.lastWidth = Math.round(this.el.clientWidth);
      if (reduce.matches || !this.originals.length || !this.lastWidth) return;
      if (getComputedStyle(this.track).display === 'grid') return;

      this.gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.templates = this.originals.map((node) => {
        const copy = node.cloneNode(true);
        copy.removeAttribute('data-shopify-editor-block');
        copy.setAttribute('aria-hidden', 'true');
        copy.querySelectorAll('a, button').forEach((a) => a.setAttribute('tabindex', '-1'));
        return copy;
      });
      this.count = this.templates.length;
      this.slots = [];
      this.total = 0;
      this.headIdx = 0;
      this.nextIdx = 0;
      this.offset = 0;

      this.track.replaceChildren();
      this.el.classList.add('is-live');
      this.live = true;

      this.fillRight();
      if (this.dir < 0) {
        this.prependOne();
        this.offset = this.slots[0];
      }
      this.apply();
    }

    idx(i) {
      return ((i % this.count) + this.count) % this.count;
    }

    appendOne() {
      const node = this.templates[this.idx(this.nextIdx)].cloneNode(true);
      this.track.append(node);
      const slot = node.getBoundingClientRect().width + this.gap;
      this.slots.push(slot);
      this.total += slot;
      this.nextIdx = this.idx(this.nextIdx + 1);
    }

    prependOne() {
      this.headIdx = this.idx(this.headIdx - 1);
      const node = this.templates[this.headIdx].cloneNode(true);
      this.track.prepend(node);
      const slot = node.getBoundingClientRect().width + this.gap;
      this.slots.unshift(slot);
      this.total += slot;
      this.offset += slot;
      return slot;
    }

    fillRight() {
      const need = this.el.clientWidth + BUFFER;
      let guard = 0;
      while (this.total - this.offset < need && guard++ < 60) this.appendOne();
    }

    apply() {
      this.track.style.transform = 'translate3d(' + (-this.offset).toFixed(2) + 'px,0,0)';
    }

    step(dt) {
      if (!this.live || !this.visible || this.hover) return;
      this.offset += this.dir * this.speed * dt;

      let guard = 0;
      while (this.slots.length > 1 && this.offset >= this.slots[0] && guard++ < 20) {
        const slot = this.slots.shift();
        this.track.firstElementChild.remove();
        this.offset -= slot;
        this.total -= slot;
        this.headIdx = this.idx(this.headIdx + 1);
      }
      guard = 0;
      while (this.offset < 0 && guard++ < 20) this.prependOne();

      this.fillRight();

      const need = this.el.clientWidth + BUFFER;
      guard = 0;
      while (this.slots.length > 2 && this.total - this.slots[this.slots.length - 1] - this.offset >= need && guard++ < 20) {
        const slot = this.slots.pop();
        this.track.lastElementChild.remove();
        this.total -= slot;
        this.nextIdx = this.idx(this.nextIdx - 1);
      }
      this.apply();
    }
  }

  const frame = (now) => {
    const dt = Math.min(now - last, 100) / 1000;
    last = now;
    tickers.forEach((ticker) => {
      if (!ticker.el.isConnected) { tickers.delete(ticker); return; }
      ticker.step(dt);
    });
    requestAnimationFrame(frame);
  };

  const start = () => {
    if (running) return;
    running = true;
    last = performance.now();
    requestAnimationFrame(frame);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-marquee]').forEach((el) => {
      if (el.dataset.marqueeReady) return;
      el.dataset.marqueeReady = '1';
      tickers.add(new Ticker(el));
    });
    if (tickers.size) start();
  };

  const rebuild = () => tickers.forEach((ticker) => ticker.setup());

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  /* po wczytaniu czcionek szerokości elementów się zmieniają: zbuduj taśmy jeszcze raz */
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(rebuild);
  window.addEventListener('load', rebuild);
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
